import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { type BoardTaskCategory, type TaskCreatorRole } from '../../../common/types/task';
import { type FetchTasksFilters } from '../../api/tasksBoardTypes';

export type QuickFilterKey = 'ALL' | 'MY' | 'ASSIGNED' | 'NEW' | 'CRITICAL' | 'MEDIUM' | 'LOW' | 'EXPIRED';
export type OwnershipFilter = 'MY' | 'ASSIGNED' | 'NEW';
export type PriorityFilter = 'CRITICAL' | 'MEDIUM' | 'LOW';

export interface BoardFiltersState {
  quick: {
    ownership: OwnershipFilter | null;
    golden: boolean;
    priority: PriorityFilter | null;
    expired: boolean;
  };

  header: {
    team: {
      id: number;
      name: string;
    } | null;
    creator: {
      id: number;
      name: string;
    } | null;
    assignee: {
      id: number;
      name: string;
    } | null;
    creatorRole: TaskCreatorRole | null;
    search: string;
    expiredOnly: boolean;
    range: FetchTasksFilters['range'] | null;
    rangeBy: FetchTasksFilters['rangeBy'];
    category: BoardTaskCategory | null;
  };
}

export const boardFiltersInitialState: BoardFiltersState = {
  quick: {
    ownership: null,
    golden: false,
    priority: null,
    expired: false,
  },

  header: {
    team: null,
    creator: null,
    assignee: null,
    creatorRole: null,
    search: '',
    expiredOnly: false,
    range: null,
    rangeBy: 'created',
    category: null,
  },
};

export const boardFiltersSlice = createSlice({
  name: 'boardFilters',
  initialState: boardFiltersInitialState,
  reducers: {
    resetQuickFilters(state) {
      state.quick.ownership = null;
      state.quick.priority = null;
      state.quick.expired = false;
      state.quick.golden = false;
    },
    setOwnershipFilter(state, action: PayloadAction<OwnershipFilter>) {
      const value = action.payload;

      state.quick.ownership = state.quick.ownership === value ? null : value;

      if (value === 'MY' && state.quick.ownership) {
        state.header.team = null;
        state.header.team = null;
      }
    },
    toggleGoldenFilter(state) {
      state.quick.golden = !state.quick.golden;
    },
    setPriorityFilter(state, action: PayloadAction<PriorityFilter>) {
      const value = action.payload;

      state.quick.priority = state.quick.priority === value ? null : value;
    },
    toggleExpiredFilter(state) {
      state.quick.expired = !state.quick.expired;
    },
    setHeaderTeam(state, action: PayloadAction<{ id: number; name: string } | null>) {
      state.header.team = action.payload;
      state.header.creator = null;

      if (state.quick.ownership === 'MY') {
        state.quick.ownership = null;
      }
    },
    setHeaderCreator(state, action: PayloadAction<{ id: number; name: string } | null>) {
      state.header.creator = action.payload;

      if (state.quick.ownership === 'MY') {
        state.quick.ownership = null;
      }
    },
    setHeaderAssignee(state, action: PayloadAction<{ id: number; name: string } | null>) {
      state.header.assignee = action.payload;
    },
    setCreatorRole(state, action: PayloadAction<TaskCreatorRole | null>) {
      state.header.creatorRole = action.payload;

      if (action.payload) {
        state.header.creator = null;
      }
    },
    setSearch(state, action: PayloadAction<string>) {
      state.header.search = action.payload;
    },
    setExpiredOnlyFilter(state, action: PayloadAction<boolean>) {
      state.header.expiredOnly = action.payload;
    },
    setDateRangeFilter(state, action: PayloadAction<[string, string] | null>) {
      state.header.range = action.payload;
    },
    setDateRangeByFilter(state, action: PayloadAction<FetchTasksFilters['rangeBy']>) {
      state.header.rangeBy = action.payload;
    },
    setCategoryFilter(state, action: PayloadAction<BoardFiltersState['header']['category']>) {
      state.header.category = action.payload;
    },
  },
});

export const boardFiltersReducer = boardFiltersSlice.reducer;

export const {
  resetQuickFilters,
  setOwnershipFilter,
  toggleGoldenFilter,
  setPriorityFilter,
  toggleExpiredFilter,
  setHeaderTeam,
  setHeaderCreator,
  setHeaderAssignee,
  setCreatorRole,
  setSearch,
  setExpiredOnlyFilter,
  setDateRangeFilter,
  setDateRangeByFilter,
  setCategoryFilter,
} = boardFiltersSlice.actions;
